type ProfileValue = string | number | null | undefined;

export interface ProgramIdentity {
  program: string;
  semester: string;
  group: string;
  years: string;
  effective_date?: string;
}

export interface StudentIdentity {
  program: string;
  semester: string | number;
  group: string;
  year: string | number;
}

const ROMAN_SEMESTERS = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

export function normalizeSemester(value?: ProfileValue): string {
  const token = String(value ?? '').trim().replace(/^semester\s*/i, '').trim().toUpperCase();
  const numeric = token.match(/^(\d+)(?:ST|ND|RD|TH)?$/);
  return numeric ? ROMAN_SEMESTERS[Number(numeric[1])] ?? String(Number(numeric[1])) : token;
}

export function normalizeGroup(value?: ProfileValue): string {
  return String(value ?? '').trim().replace(/^group\s*/i, '').trim().toUpperCase();
}

export function normalizeBatch(value?: ProfileValue): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[\u2010-\u2015\u2212]/g, '-')
    .replace(/[()]/g, ' ')
    .replace(/\s*-\s*/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeProgram(value: ProfileValue): string {
  return String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function matchesBatch(batch: string, requested: string): boolean {
  const normalized = normalizeBatch(batch);
  if (normalized === requested) return true;
  // Older profiles may contain just the admission year, never a year inside
  // another cohort's degree range.
  return /^\d{4}$/.test(requested) && normalized.match(/^\d{4}\b/)?.[0] === requested;
}

/** Keep the source label for forms, resolving a legacy year only when unique. */
export function findMatchingBatch(batches: readonly string[], value?: ProfileValue): string | null {
  const requested = normalizeBatch(value);
  if (!requested) return null;
  const matches = batches.filter(batch => matchesBatch(batch, requested));
  return new Set(matches.map(normalizeBatch)).size === 1 ? matches[0] : null;
}

/** Resolve only the saved cohort; an unavailable semester needs a profile update. */
export function findUserProgram<T extends ProgramIdentity>(
  programs: readonly T[],
  preferences?: StudentIdentity | null,
): T | null {
  if (!preferences) return null;
  const program = normalizeProgram(preferences.program);
  const semester = normalizeSemester(preferences.semester);
  const group = normalizeGroup(preferences.group);
  const batch = normalizeBatch(preferences.year);
  if (!program || !semester || !group || !batch) return null;

  // Department is intentionally excluded: older imports saved all bachelor
  // profiles as Business Administration, including computing and English.
  const matches = programs.filter(candidate =>
    normalizeProgram(candidate.program) === program &&
    normalizeSemester(candidate.semester) === semester &&
    normalizeGroup(candidate.group) === group &&
    matchesBatch(candidate.years, batch),
  );
  if (!findMatchingBatch(matches.map(candidate => candidate.years), batch)) return null;

  const dateValue = (date?: string) => {
    const timestamp = date ? Date.parse(date) : NaN;
    return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
  };
  return [...matches].sort((a, b) => dateValue(b.effective_date) - dateValue(a.effective_date))[0] ?? null;
}

/** Historical finals must not graduate a cohort with a newer timetable. */
export function hasCompletedFinalSemester(
  programs: readonly ProgramIdentity[],
  preferences: StudentIdentity | null | undefined,
  completion: { endedOn: Date } | null,
): boolean {
  if (!preferences || normalizeSemester(preferences.semester) !== 'VIII' || !completion) return false;
  const batch = normalizeBatch(preferences.year);
  if (!batch || !normalizeProgram(preferences.program) || !Number.isFinite(completion.endedOn.getTime())) return false;

  // Check the cohort even when a saved group no longer exists. That student
  // needs migration, not a graduation screen based on an unrelated exam term.
  const cohort = programs.filter(candidate =>
    normalizeProgram(candidate.program) === normalizeProgram(preferences.program) &&
    matchesBatch(candidate.years, batch),
  );
  return !cohort.some(candidate => {
    const effectiveDate = candidate.effective_date ? Date.parse(candidate.effective_date) : NaN;
    return !Number.isFinite(effectiveDate) || effectiveDate > completion.endedOn.getTime();
  });
}
